import { useState } from 'react';
import { useWeb3Modal } from '@web3modal/react';

import { useWalletView } from '#/features/wallet/wallet-context';
import { logException } from '#/features/logging/logging';
import * as systemUtils from '#/features/system/utils';

import * as Alert from '#/components/common/alert';

import InProgressIcon from '#/assets/InProgressIcon';
import WalletConnectIconOutlined from '#/assets/WalletConnectIconOutlined';

import PrimaryButton from './PrimaryButton';

export default function WalletConnectButton() {
  const walletView = useWalletView();
  const walletConnectModal = useWeb3Modal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const isBusy =
    isLoading || walletConnectModal.isOpen || walletView.status !== 'idle';

  const openWalletConnectModal = () => {
    (async () => {
      setIsLoading(true);
      // Unhandleable errors in WalletConnect can cause this to hang.
      // indefinitely. For example, this can happen when a JS chunk fails
      // to load: https://github.com/WalletConnect/web3modal/issues/1026
      await withTimeout(walletConnectModal.open, 5_000, () => {
        setError(
          'WalletConnect is taking too long to open. ' +
            'Please refresh the page and try again.',
        );
        // Mitigate potential issues with full LocalStorage, which can lead to user not being able to recover on one's own
        systemUtils.resetSystemState();
      });
      setIsLoading(false);
      setError('');
    })().catch((err) => {
      const message = 'Unexpected error opening WalletConnect modal';
      logException(new Error(message, { cause: err }));
    });
  };

  return (
    <>
      <PrimaryButton disabled={isBusy} onClick={openWalletConnectModal}>
        {isBusy ? (
          <InProgressIcon size={22} />
        ) : (
          <>
            <WalletConnectIconOutlined />
            Choose Wallet
          </>
        )}
      </PrimaryButton>
      {error.length > 0 && <Alert.Error>{error}</Alert.Error>}
    </>
  );
}

/**
 * Executes an operation with a timeout. When the timeout is reached,
 * the `onTimeout` callback is executed.
 *
 * @param operation Operation to execute
 * @param duration Timeout duration in milliseconds
 * @param onTimeout Callback to execute when the timeout is reached
 */
async function withTimeout(
  operation: () => Promise<void>,
  duration: number,
  onTimeout: () => void,
) {
  const timeout = setTimeout(onTimeout, duration);
  await operation();
  clearTimeout(timeout);
}
